import { queryOptions } from '@tanstack/react-query'

import { listAssets } from '@/api/assets'
import { fetchJson, path } from '@/api/client'
import type { StoryGenerationMode } from '@/schema/settings'
import type { LessonCard } from '@/schema/lessons'

/**
 * `/api/stories*` and `/api/story-presets` (docs/SKEL-STORY.md §3.4).
 *
 * A cast member is a puppet: `skel:<ULID>`, or — the convenience path — a catalog asset uid
 * (`char:…` / `obj:…`) that the server rigs on the fly while the story is written.
 * In spritesheet mode the cast uses catalog character uids with ready sprite clips.
 */

export type AgeBand = '3-4' | '5-6' | '7-8'
export type CastRole = 'hero' | 'friend'
/** How long a story runs (`backend/app/story/lengths.py`): Short ~30 s in one part, Medium ~2 min in 4 parts, Long ~3–5 min in 6–8 parts. */
export type StoryLength = 'short' | 'medium' | 'long'
export const STORY_LENGTHS: readonly StoryLength[] = ['short', 'medium', 'long']
export const STORY_TOPIC_IDS = ['story', 'learn', 'grow', 'bedtime', 'today'] as const
export type StoryTopic = (typeof STORY_TOPIC_IDS)[number]

export interface StoryCastRequest {
  uid: string
  role?: CastRole
  speaks?: boolean
  /** Optional display name override (the puppet's own name otherwise). */
  name?: string
  /** Legacy field some older rows still carry. */
  pronoun?: 'she' | 'he' | 'they'
}

/**
 * A thing in the story (§6 Q1/Q10): a catalog object uid (`obj:toys/kite`) — the server rigs it if
 * it cannot move yet, like a catalog cast pick — or a `skel:<ULID>` object puppet.
 */
export interface StoryPropRequest {
  uid: string
}

export interface StoryRequest {
  authoring_version?: 'events@1'
  generation_mode?: StoryGenerationMode
  prompt: string
  age_band: AgeBand
  world?: string
  stage_uid?: string
  cast: StoryCastRequest[]
  seed?: number
  listener_name?: string
  theme?: string
  topic?: StoryTopic
  learning_subject?: string
  learning_objective_id?: string
  learning_objective_revision?: number
  /**
   * The things the wizard found in the idea (minus the ones the parent removed, plus the ones added).
   * Omitted when the wizard has no settled answer: the server then finds them in `prompt` itself.
   * `[]` means "no things" on purpose.
   */
  props?: StoryPropRequest[]
  /** "Living background" (Q7): scenery puppets that sway and drift behind the actors. Server default on. */
  scenery?: boolean
  /** How long the story runs. Server default `short`, so the wizard only sends it for a longer story. */
  length?: StoryLength
  /** Start the new story bookmarked ("Try again" on a bookmarked story). Server default false. */
  bookmarked?: boolean
}

/** One part (chapter) of a Medium or Long story. */
export interface StoryPart {
  id: string
  kind: string
  title: string
}

export type StoryStatus = 'draft' | 'generating' | 'ready' | 'failed' | 'archived'

/**
 * How far a longer story has streamed (`stories.options_json.stream`, docs/STORY-STREAMING.md): the
 * server publishes its bundle part by part while it is still `generating`, so the player can start
 * on part 1. `streaming` = more parts are coming; `paused` = the AI usage window was hit and the
 * job goes on at `resume_at`; `failed` = it stopped (`parts_total == parts_ready`, nothing more
 * comes); `complete` = the whole story is made.
 */
export interface StoryStream {
  parts_ready: number
  parts_total: number
  state: 'streaming' | 'paused' | 'failed' | 'complete'
  resume_at: string | null
}

export interface StoryRow {
  /** Actual saved authoring provenance; never inferred from current settings. */
  generation_models?: string[]
  generation_provider?: string | null
  generation_origin?: 'ai' | 'editorial' | 'mixed' | 'unknown'
  authoring_version?: 'events@1'
  topic?: StoryTopic
  learning_subject?: string | null
  learning_objective_id?: string
  learning_objective_revision?: number
  learning_objective?: LessonCard
  /** Older rows were made with bones. */
  generation_mode?: StoryGenerationMode
  uid: string
  title: string
  prompt: string
  age_band: string
  world?: string | null
  cast: StoryCastRequest[]
  status: StoryStatus
  job_id?: number | null
  /** Why it failed (§3.4). Older servers put it in `validation.errors`. */
  error?: string | null
  listener_name?: string | null
  /** What the request carried (Q10), when the server keeps it on the row. */
  props?: StoryPropRequest[] | null
  scenery?: boolean | null
  /** How long it was asked to run (older servers: absent = short). */
  length?: StoryLength
  /** A longer story's parts, in order (empty for a one-part story). */
  parts?: StoryPart[]
  /** Seconds it plays: the bundle's own length once made (measured narration), else the script's estimate. */
  runtime_s?: number | null
  /** Seconds the made story plays (its bundle, every part end to end); null before it is made. */
  duration_s?: number | null
  /** Detail only: wall time since the first job start, including retries and narration. */
  generation_seconds?: number | null
  generation_state?: 'completed' | 'running' | 'queued' | 'failed' | 'cancelled' | 'unavailable'
  /** Recorded story AI total; partial and estimated amounts are labelled separately. */
  story_cost_usd?: number | null
  story_cost_basis?: 'reported' | 'estimated' | 'mock' | 'unavailable'
  story_cost_partial?: boolean
  /** API cost of recorded narration, excluding local compute and hosting. */
  tts_cost_usd?: number | null
  tts_cost_basis?: 'local' | 'free' | 'local_and_free' | 'unavailable'
  /** How many parts (scenes) the made story plays; null before it is made. */
  scenes?: number | null
  /**
   * The story's card picture (`GET /api/stories/<uid>/thumb.webp?v=<key>`): its first part's stage
   * with everyone in their opening places, 640×360. Null before it is made.
   */
  thumb_url?: string | null
  cost_usd: number
  /** `scene1_ready`: part 1 can be watched — while `generating` too, once the server streamed it. */
  summary: { scene1_ready: boolean; status: string }
  /** The part-by-part stream; null (older servers: absent) for a story that never streamed. */
  stream?: StoryStream | null
  script?: unknown
  validation: { errors: unknown[]; warnings: unknown[] }
  created_at: string
  updated_at: string
  /** The parent bookmarked it (older servers: absent = not bookmarked). */
  bookmarked?: boolean
  /** When it was bookmarked; null when it is not. */
  bookmarked_at?: string | null
}

export interface StoryWorld {
  world: string
  stages_ready: number
  heroes_ready?: number
}

/** A "How long?" choice of the wizard (`GET /api/story-presets` → `lengths`). */
export interface StoryLengthPreset {
  id: StoryLength
  label: string
  /** "about 2 minutes · 3–4 parts" */
  blurb: string
  runtime_s: [number, number]
  parts: [number, number]
  /** How long making it usually takes, seconds. */
  make_s: [number, number]
  /** How long until its first part can be watched, seconds (a longer story streams; absent = unknown). */
  starts_s?: [number, number] | null
  /** What a story of this length has cost so far, or null before there is enough data. */
  usd: number | null
  default?: boolean
}

export interface StoryPreset {
  event_generation_enabled?: boolean
  generation_mode?: StoryGenerationMode
  /** Older servers leave it out: the wizard then uses `DEFAULT_LENGTHS`. */
  lengths?: StoryLengthPreset[]
  worlds: StoryWorld[]
  age_bands: Array<{ band: string; max_cast: number; max_heroes?: number }>
  /** Voice roles → `{voice, speed, sample_text}` (the keys are the puppet `voice_role` choices). */
  voices: Record<string, unknown>
}

export function getStoryPresets(signal?: AbortSignal) {
  return fetchJson<StoryPreset>('/api/story-presets', { signal })
}
export function createStory(body: StoryRequest) {
  return fetchJson<{ story: StoryRow; job_id: number }>('/api/stories', { method: 'POST', body })
}
export function getStory(uid: string, signal?: AbortSignal) {
  return fetchJson<StoryRow>(path`/api/stories/${uid}`, { signal })
}
export interface StoryListResponse {
  items: StoryRow[]
  total: number
}
/** List order: newest first (the default), or bookmarked stories first (each group newest first). */
export type StorySort = 'newest' | 'bookmarked'
export interface StoryListFilters {
  /** `true`: only bookmarked stories; `false`: only the others; absent: all. */
  bookmarked?: boolean
  sort?: StorySort
}
export function listStories(status?: string, signal?: AbortSignal, filters: StoryListFilters = {}) {
  return fetchJson<StoryListResponse>('/api/stories', { query: { status, bookmarked: filters.bookmarked, sort: filters.sort }, signal })
}
/** `PUT` bookmarks (idempotent: the first `bookmarked_at` is kept), `DELETE` removes the bookmark; `404` for an unknown story. */
export interface StoryBookmark {
  uid: string
  bookmarked: boolean
  bookmarked_at: string | null
}
export function setStoryBookmark(uid: string, bookmarked: boolean) {
  return fetchJson<StoryBookmark>(path`/api/stories/${uid}/bookmark`, { method: bookmarked ? 'PUT' : 'DELETE' })
}
/** `204`; `404 not_found` when the story is already gone. Also cancels its job if it is still generating. */
export function deleteStory(uid: string) {
  return fetchJson<void>(path`/api/stories/${uid}`, { method: 'DELETE' })
}
/** Bulk delete; unknown uids are skipped, so `deleted` may be less than `uids.length`. */
export function deleteStories(uids: string[]) {
  return fetchJson<{ deleted: number }>('/api/stories/delete', { method: 'POST', body: { uids } })
}
export function bundleUrl(uid: string) {
  return path`/api/stories/${uid}/bundle/bundle.json`
}
export function exportUrl(uid: string) {
  return path`/api/stories/${uid}/export.zip`
}
export interface RefreshStoryAssetsResponse {
  preserved_verified_pins?: boolean
  notice?: string
  story: StoryRow
  bundle_sha256?: string | null
}
/** Re-pin puppet assets from the store (updated animations/textures), re-bake bundle and refresh narration. */
export function refreshStoryAssets(uid: string) {
  return fetchJson<RefreshStoryAssetsResponse>(path`/api/stories/${uid}/refresh-assets`, { method: 'POST' })
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

/** The failure sentence of a story row, from `error` or the first validation error. */
export function storyError(row: Pick<StoryRow, 'error' | 'validation'>): string | null {
  if (row.error) return row.error
  const first = row.validation?.errors?.[0]
  if (typeof first === 'string') return first
  if (first && typeof first === 'object' && 'message' in first) return String((first as { message: unknown }).message)
  return null
}

/**
 * A story still being made can already be watched: the server streamed its first part
 * (`stream.parts_ready ≥ 1`), or — the same fact from the summary — `scene1_ready` flipped before
 * `ready`. Always false for a story that never streamed (a Short one, an older server).
 */
export function streamReady(row: Pick<StoryRow, 'stream' | 'summary'> | null | undefined): boolean {
  return (row?.stream?.parts_ready ?? 0) >= 1 || !!row?.summary?.scene1_ready
}

/** "Part 1 of 7 is ready", "Parts 1–3 of 7 are ready", "All 7 parts are ready"; null before any. */
export function partsReadyLine(stream: Pick<StoryStream, 'parts_ready' | 'parts_total'> | null | undefined): string | null {
  const k = stream?.parts_ready ?? 0
  const n = Math.max(k, stream?.parts_total ?? 0)
  if (k < 1) return null
  if (k >= n) return n === 1 ? 'Part 1 is ready' : `All ${n} parts are ready`
  return k === 1 ? `Part 1 of ${n} is ready` : `Parts 1–${k} of ${n} are ready`
}

/** The request that made a story, to make it again ("Try again", "Make another like this"). */
export function requestOf(row: StoryRow): StoryRequest {
  const band = (['3-4', '5-6', '7-8'] as const).find((b) => b === row.age_band) ?? '3-4'
  return {
    prompt: row.prompt,
    age_band: band,
    generation_mode: row.generation_mode ?? 'bones',
    ...(row.authoring_version === 'events@1' ? { authoring_version: row.authoring_version } : {}),
    // Every new story is a free one: a story made with a retired topic is made again as a free story.
    world: row.world || undefined,
    cast: (row.cast ?? []).map((c) => ({ uid: c.uid, ...(c.role ? { role: c.role } : {}), ...(c.speaks !== undefined ? { speaks: c.speaks } : {}) })),
    listener_name: row.listener_name || undefined,
    ...(Array.isArray(row.props) ? { props: row.props.flatMap((p) => (p && typeof p.uid === 'string' ? [{ uid: p.uid }] : [])) } : {}),
    ...(typeof row.scenery === 'boolean' ? { scenery: row.scenery } : {}),
    ...(row.length && row.length !== 'short' && STORY_LENGTHS.includes(row.length) ? { length: row.length } : {}),
  }
}

/** The lengths the wizard offers when the server does not list them (the backend's own table). */
export const DEFAULT_LENGTHS: StoryLengthPreset[] = [
  { id: 'short', label: 'Short', blurb: 'about 30 seconds · 1 part', runtime_s: [20, 45], parts: [1, 1], make_s: [20, 60], usd: null, default: true },
  { id: 'medium', label: 'Medium', blurb: 'about 2 minutes · 4 parts', runtime_s: [90, 150], parts: [4, 4], make_s: [60, 150], usd: null },
  { id: 'long', label: 'Long', blurb: 'about 3–5 minutes · 6–8 parts', runtime_s: [180, 300], parts: [6, 8], make_s: [100, 240], usd: null },
]

/** "about 40 seconds", "about 2 minutes", "about 3–5 minutes" for a span of seconds. */
export function aboutTime([lo, hi]: readonly [number, number]): string {
  const words = (s: number): string => (s < 90 ? `${Math.round(s / 5) * 5} seconds` : `${Math.round(s / 60)} minute${Math.round(s / 60) === 1 ? '' : 's'}`)
  if (hi < 90) return `about ${words(Math.round((lo + hi) / 2))}`
  const a = Math.max(1, Math.round(lo / 60))
  const b = Math.max(1, Math.round(hi / 60))
  return a === b ? `about ${a} minute${a === 1 ? '' : 's'}` : `about ${a}–${b} minutes`
}

/**
 * Whole seconds of a made story's length, floored the way the player's own clock is
 * (`player/controls.ts#formatTime`: "3:46 / 3:46" at the end), so a card never says 3:47 for a
 * story whose player stops at 3:46. At least 1 for any story that plays at all.
 */
function wholeSeconds(seconds: number): number {
  return Math.max(1, Math.floor(seconds + 1e-6))
}

/** "2 min 10 s", "45 s": how long a made story plays. */
export function runtimeLabel(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null
  const s = wholeSeconds(seconds)
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  const r = s % 60
  return r ? `${m} min ${r} s` : `${m} min`
}

/** "0:31", "4:12", "1:02:05": a made story's length the way a video player writes it. */
export function clockLabel(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null
  const s = wholeSeconds(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`
}

/** "Plays for 4 minutes 12 seconds": `clockLabel` for a screen reader. */
export function spokenLength(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null
  const s = wholeSeconds(seconds)
  const m = Math.floor(s / 60)
  const r = s % 60
  const words = [m ? `${m} minute${m === 1 ? '' : 's'}` : '', r ? `${r} second${r === 1 ? '' : 's'}` : ''].filter(Boolean)
  return `Plays for ${words.join(' ')}`
}

/** How long a story row plays: the made bundle's length, else the estimate. */
export function storySeconds(row: Pick<StoryRow, 'duration_s' | 'runtime_s'>): number | null {
  return row.duration_s ?? row.runtime_s ?? null
}

/** How many parts a story row plays: the made bundle's scenes, else its planned parts. */
export function storyPartCount(row: Pick<StoryRow, 'scenes' | 'parts'>): number {
  return row.scenes ?? row.parts?.length ?? 0
}

/** "underwater" → "Underwater". */
export function worldLabel(world: string): string {
  const t = world.replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : world
}

/**
 * One picture per world, for the wizard's place cards: the first still background of each world by
 * name, else the first moving one (most worlds only have moving plates). `GET /api/assets` pages of
 * 200: ~150 stills, ~300 moving plates.
 */
export async function worldPlates(signal?: AbortSignal): Promise<Record<string, string>> {
  const settle = (p: Promise<Awaited<ReturnType<typeof listAssets>>>) => p.then((r) => r.items).catch(() => [])
  const [still, live1, live2] = await Promise.all([
    settle(listAssets({ kind: 'background', size: 200, sort: 'name' }, signal)),
    settle(listAssets({ kind: 'live_background', size: 200, sort: 'name', page: 1 }, signal)),
    settle(listAssets({ kind: 'live_background', size: 200, sort: 'name', page: 2 }, signal)),
  ])
  const out: Record<string, string> = {}
  for (const a of [...still, ...live1, ...live2]) {
    if (!a.world || out[a.world] || a.hidden || a.enabled === false) continue
    const src = a.media?.thumb
    if (src) out[a.world] = src
  }
  return out
}

export const storyPresetsQuery = () =>
  queryOptions({
    queryKey: ['stories', 'presets', 'wizard'] as const,
    queryFn: ({ signal }) => getStoryPresets(signal),
    staleTime: 60_000,
  })

export const worldPlatesQuery = () =>
  queryOptions({
    queryKey: ['stories', 'world-plates'] as const,
    queryFn: ({ signal }) => worldPlates(signal),
    staleTime: 5 * 60_000,
    retry: false,
  })
